import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { api } from '@/lib/api';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  task_id: string | null;
  supplier_order_id: string | null;
  created_at: string;
  // T2.4 dedupe fields. dedupe_count > 1 means the same group_key has fired
  // multiple times inside the rollup window; updated_at is the latest bump.
  dedupe_count?: number;
  updated_at?: string;
  group_key?: string | null;
}

export default function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Poll for unread count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await api.get<{ unread: number }>('/admin/agent/notifications/count');
        setUnreadCount(data.unread);
      } catch {
        // Silently fail — notification count is not critical
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await api.get<{ notifications: Notification[] }>('/admin/agent/notifications?limit=10');
      setNotifications(data.notifications);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) loadNotifications();
  };

  const markRead = async (id: string) => {
    try {
      await api.patch(`/admin/agent/notifications/${id}/read`, {});
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const markAllRead = async () => {
    try {
      await api.post('/admin/agent/notifications/read-all', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  // T2.4: archive removes a row from the default inbox (soft-delete; row
  // stays in DB for history). Drops unread count only if it was unread.
  const archive = async (e: React.MouseEvent, n: Notification) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await api.post(`/admin/agent/notifications/${n.id}/archive`, {});
      setNotifications((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.read) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // ignore
    }
  };

  const typeColors: Record<string, string> = {
    escalation: 'bg-red-100 text-red-700',
    order_failure: 'bg-orange-100 text-orange-700',
    tracking_issue: 'bg-yellow-100 text-yellow-700',
    return_dispute: 'bg-purple-100 text-purple-700',
    system: 'bg-blue-100 text-blue-700',
    info: 'bg-stone-100 text-stone-700',
  };

  const getLinkForNotification = (n: Notification): string | null => {
    if (n.task_id) return `/admin/agent/tasks/${n.task_id}`;
    if (n.supplier_order_id) return '/admin/supplier-orders';
    return null;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggle}
        className="relative p-2 text-stone-600 hover:text-stone-900 rounded-md hover:bg-stone-100 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-lg border border-stone-200 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <h3 className="text-sm font-semibold text-stone-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-stone-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-stone-500">No notifications</div>
            ) : (
              notifications.map((n) => {
                const link = getLinkForNotification(n);
                const repeatCount = (n.dedupe_count ?? 1) > 1 ? n.dedupe_count : null;
                const content = (
                  <div
                    key={n.id}
                    className={`group relative px-4 py-3 border-b border-stone-50 hover:bg-stone-50 cursor-pointer ${!n.read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => !n.read && markRead(n.id)}
                  >
                    <div className="flex items-start gap-2 pr-6">
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded ${typeColors[n.type] || typeColors.info}`}>
                        {n.type.replace(/_/g, ' ')}
                      </span>
                      {repeatCount && (
                        <span
                          className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-stone-200 text-stone-700"
                          title="This event has fired multiple times — rolled up into one row"
                        >
                          ×{repeatCount}
                        </span>
                      )}
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-sm font-medium text-stone-900 mt-1 line-clamp-1">{n.title}</p>
                    <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-stone-400 mt-1">
                      {new Date(n.updated_at || n.created_at).toLocaleString()}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => archive(e, n)}
                      className="absolute top-2 right-2 p-1 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-200 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Archive notification"
                      title="Archive"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );

                return link ? (
                  <Link key={n.id} to={link} onClick={() => setIsOpen(false)}>
                    {content}
                  </Link>
                ) : (
                  <div key={n.id}>{content}</div>
                );
              })
            )}
          </div>

          <Link
            to="/admin/agent/notifications"
            onClick={() => setIsOpen(false)}
            className="block px-4 py-2.5 text-center text-xs text-blue-600 hover:text-blue-800 border-t border-stone-100"
          >
            View all notifications
          </Link>
        </div>
      )}
    </div>
  );
}
